import { Controller } from "@hotwired/stimulus"

// In-lesson Poll runtime (TMS-809). Steps through the block's poll questions, posts the
// learner's answer to submit_poll, and reveals the peer results in place.
//
// This is the only in-lesson block that persists anything, so it is also the only one
// that talks to the server: the reader sets data-turbo="false", so the answer goes out
// as a fetch with the CSRF meta token rather than a form post, and the results are
// revealed without a page load — a redirect would lose the learner's place mid-lesson.
//
// Every state is already in the DOM, server-rendered. This controller only toggles which
// one is visible and writes bar widths, percentages, the total and the "You" pill, so no
// markup is built here and server-stored option text is never re-injected.
export default class extends Controller {
  static targets = ["question", "progressLabel", "progressFill", "previousButton", "nextButton"]
  static values = {
    submitUrl: String,
    locked: Boolean
  }

  connect() {
    this.questionIndex = 0
    this.showQuestion(0)
  }

  select(event) {
    const question = event.currentTarget.closest("[data-poll-target='question']")
    const submit = question?.querySelector("[data-poll-submit]")
    if (!submit) return

    this.hideError(question)
    submit.disabled = this.lockedValue
  }

  async submit(event) {
    event.preventDefault()
    if (this.lockedValue) return

    const question = event.currentTarget.closest("[data-poll-target='question']")
    const chosen = question?.querySelector(".academy-poll-option-input:checked")
    if (!question || !chosen) return

    const submit = question.querySelector("[data-poll-submit]")
    if (submit) submit.disabled = true
    this.hideError(question)

    const payload = await this.postAnswer(question.dataset.pollKey, chosen.value)
    if (!payload) {
      this.showError(question, "We couldn't record your answer. Please try again.")
      if (submit) submit.disabled = false
      return
    }

    this.applyResults(question, payload)
    this.revealResults(question)
  }

  // Returning to the ballot leaves the learner's answer selected, so submitting again
  // without changing anything is a no-op rather than a surprise. Submit comes back
  // enabled with it — a disabled button beside a visibly chosen option reads as broken.
  changeAnswer(event) {
    event.preventDefault()
    if (this.lockedValue) return

    const question = event.currentTarget.closest("[data-poll-target='question']")
    if (!question) return

    this.setHidden(question, "[data-poll-results]", true)
    this.setHidden(question, "[data-poll-ballot]", false)

    const chosen = question.querySelector(".academy-poll-option-input:checked")
    const submit = question.querySelector("[data-poll-submit]")
    if (submit) submit.disabled = !chosen
    chosen?.focus()
  }

  previousQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.questionIndex - 1)
  }

  nextQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.questionIndex + 1)
  }

  async postAnswer(pollKey, optionKey) {
    if (!this.hasSubmitUrlValue) return null

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin",
        body: JSON.stringify({ poll_key: pollKey, option_key: optionKey })
      })
      const data = await response.json().catch(() => ({}))

      return response.ok && data.success ? data : null
    } catch (error) {
      return null
    }
  }

  // Percentages are computed server-side (largest remainder, so they sum to 100); this
  // only paints them. With peer results off the bars stay empty and unlabelled — the
  // learner just sees their own choice held.
  applyResults(question, payload) {
    const showResults = question.dataset.pollShowResults === "true"
    const byKey = new Map((payload.options || []).map((option) => [option.key, option]))

    const total = question.querySelector("[data-poll-total]")
    if (total && showResults) {
      total.textContent = `${payload.total} ${payload.total === 1 ? "Response" : "Responses"}`
    }

    question.querySelectorAll("[data-poll-bar]").forEach((bar) => {
      const option = byKey.get(bar.dataset.optionKey)
      const mine = bar.dataset.optionKey === payload.chosen_option_key
      const fill = bar.querySelector("[data-poll-bar-fill]")
      const percentage = bar.querySelector("[data-poll-bar-percentage]")

      bar.classList.toggle("is-mine", mine)
      if (fill) fill.style.width = showResults && option ? `${option.percentage}%` : "0%"
      if (percentage && showResults && option) percentage.textContent = `${option.percentage}%`

      const you = bar.querySelector("[data-poll-bar-you]")
      if (you) you.hidden = !mine
    })
  }

  revealResults(question) {
    this.setHidden(question, "[data-poll-ballot]", true)
    this.setHidden(question, "[data-poll-results]", false)
  }

  showQuestion(index) {
    const questions = this.questionTargets
    if (questions.length === 0) return

    this.questionIndex = Math.min(Math.max(index, 0), questions.length - 1)
    questions.forEach((question, questionIndex) => {
      question.hidden = questionIndex !== this.questionIndex
    })

    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `Question ${this.questionIndex + 1} of ${questions.length}`
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${((this.questionIndex + 1) / questions.length) * 100}%`
    }
    if (this.hasPreviousButtonTarget) this.previousButtonTarget.disabled = this.questionIndex === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = this.questionIndex === questions.length - 1
  }

  showError(question, message) {
    const error = question.querySelector("[data-poll-error]")
    if (!error) return

    error.textContent = message
    error.hidden = false
  }

  hideError(question) {
    const error = question.querySelector("[data-poll-error]")
    if (!error) return

    error.textContent = ""
    error.hidden = true
  }

  setHidden(question, selector, hidden) {
    const element = question.querySelector(selector)
    if (element) element.hidden = hidden
  }
}
